import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { capitalize } from "lodash";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { useGetCourseOptionsFromValidCustomerBookingSelections } from "@/features/BookingWizard/components/WizardStepContainer/steps/BookingDetailsStep/controls/CourseControl/hooks/useGetCourseOptionsFromValidCustomerBookingSelections";
import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { ClockIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  HStack,
  Box,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { setStartSlotandCourseIdAtom } from "../../../../../atoms/actions/setStartSlotAndCourseIdAtom/setStartSlotAndCourseIdAtom";
import { currentUserIsBookingOwnerAtom } from "../../../../../atoms/selectors/currentUserIsBookingOwnerAtom/currentUserIsBookingOwnerAtom";
import { getBookingDurationAtom } from "../../../../../atoms/selectors/getBookingDurationAtom/getBookingDurationAtom";
import { getBookingGroupSizeAtom } from "../../../../../atoms/selectors/getBookingGroupSizeAtom/getBookingGroupSizeAtom";
import { getCourseIdAtom } from "../../../../../atoms/selectors/getCourseIdAtom/getCourseIdAtom";
import { isAlreadyPaidAtom } from "../../../../../atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import { useGetCourseOptions_CourseBucket$key } from "../hooks/__generated__/useGetCourseOptions_CourseBucket.graphql";
import { CourtsControlFragment$key } from "./__generated__/CourtsControlFragment.graphql";

type Props = {
  slotRef?: CourtsControlFragment$key;
  courseBucketRef: useGetCourseOptions_CourseBucket$key | null;
  currentCourtId: string | null;
};

export const CourtsControl = ({ slotRef }: Props) => {
  const slot = useFragment(CourtsControlFragmentString, slotRef);

  const courseId = useAtomValue(getCourseIdAtom);
  const duration = useAtomValue(getBookingDurationAtom);
  const currentGroupSize = useAtomValue(getBookingGroupSizeAtom);
  const setStartSlotAndCourseId = useSetAtom(setStartSlotandCourseIdAtom);

  const currentUserIsBookingOwner = useAtomValue(currentUserIsBookingOwnerAtom);

  const courseOptions = useGetCourseOptionsFromValidCustomerBookingSelections(
    slot ?? null,
    duration ?? 0,
    currentGroupSize ?? 0
  );

  const courtsOptions: DropdownSelectOption<string>[] = useMemo(
    () => [
      ...courseOptions.flatMap((course) =>
        course.label && course.value
          ? [
              {
                label: course.label,
                value: course.value,
                subLabel: course.description ?? undefined,
                isDisabled: course.isDisabled,
              },
            ]
          : []
      ),
    ],
    [courseOptions]
  );

  const courseNoun = getNounOverride(
    slot?.agenda?.facility?.copyOverrides?.course?.singular,
    "Course"
  );
  const facilityVenueLabel = capitalize(courseNoun);

  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  return courtsOptions.length > 0 ? (
    <VStack padding={5} gap={5} backgroundColor="surface-primary" marginTop={2}>
      <HStack alignItems="center" gap={3}>
        <Box
          backgroundColor="surface-main-primary-subtlest"
          width={24}
          height={24}
          justifyContent="center"
          alignItems="center"
          style={{ borderRadius: 4 }}
        >
          <ClockIcon />
        </Box>
        <Text>{facilityVenueLabel}</Text>
      </HStack>
      <VStack flex={1}>
        <DropdownSelect
          isViewOnly={!currentUserIsBookingOwner || isAlreadyPaid}
          hideOuterMargin
          testID="booking-courts-dropdown"
          hideChevron
          includeLeftElementInSelectedValue
          contentSizeOverride="matchTrigger"
          options={courtsOptions}
          value={courseId}
          onChange={(courtId) => {
            const selectedCourse = courseOptions.find(
              (course) => course.value === courtId
            );
            setStartSlotAndCourseId({
              startSlotId: selectedCourse?.startSlotId || "",
              courseId: selectedCourse?.value || "",
            });
          }}
        />
      </VStack>
    </VStack>
  ) : null;
};

const CourtsControlFragmentString = graphql`
  fragment CourtsControlFragment on Slot {
    id
    ... on TimeSlot {
      ...useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot
      course {
        id
        name
        description
      }
    }
    agenda {
      facility {
        copyOverrides {
          course {
            singular
          }
        }
      }
    }
  }
`;
